import { useCallback, useEffect, useMemo, useState } from 'react'
import { pickAndStoreMultiple, preload } from 'services/filestack'
import { PickerFileMetadata, PickerOptions } from 'filestack-js/build/main/lib/picker'
import { useSnackbar } from './redux'

export interface IUseFilestackMultipleFilesOptions {
  onChange: (value: PickerFileMetadata[]) => void
  onError?: (error: Error) => void
  options?: PickerOptions
  onOpen?: () => void
}

export const useFilestack = ({ onChange, onOpen, onError, options = {} }: IUseFilestackMultipleFilesOptions) => {
  const [open, setOpen] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [error, setError] = useState<Error>()
  const { showSnackbarMessage } = useSnackbar()

  const onClose = options.onClose

  useEffect(() => {
    preload()
  }, [])

  useEffect(() => {
    if (error) {
      // show snackbar first so that if the onError callback also has its own snackbar, it can overwrite this one
      showSnackbarMessage(error.message, { level: 'error' })

      if (onError) {
        onError(error)
      }
    }
  }, [error, onError, showSnackbarMessage])

  const handleCloseCancel = useCallback(() => {
    setOpen(false)
    setDialogOpen(false)

    if (typeof onClose === 'function') {
      onClose()
    }
  }, [onClose])

  const openPicker = useCallback(() => {
    setOpen(true)

    if (typeof onOpen === 'function') {
      onOpen()
    }

    pickAndStoreMultiple({
      ...options,
      onOpen: () => {
        setDialogOpen(true)
      },
      onFileUploadFailed: (data, uploadError) => {
        setError(uploadError || new Error('File upload failed. Check your connection.'))
      },
      onClose: handleCloseCancel,
    }).then(onChange)
  }, [onChange, options, handleCloseCancel, onOpen])

  return {
    isOpen: open,
    isLoading: open && !dialogOpen,
    error,
    openPicker,
  }
}

type UseUploadFileOptions = Omit<IUseFilestackMultipleFilesOptions, 'onChange'> & {
  onChange: (value: PickerFileMetadata) => void
}

export function useUploadFile(options: UseUploadFileOptions) {
  const filestackOptions = useMemo(() => ({
    ...options,
    onChange(files: PickerFileMetadata[]) {
      const file = files[0]
      if (file) {
        options.onChange(file)
      }
    },
  }), [options])

  return useFilestack(filestackOptions)
}
